import React, { useState, useEffect, useCallback } from 'react'
import { useDataProvider, useNotify, useRefresh } from 'react-admin'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Button as MuiButton,
  Alert,
  Link,
  Divider,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DeleteIcon from '@mui/icons-material/Delete'
import SendIcon from '@mui/icons-material/Send'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { sendQuestionnaire } from './SendQuestionnaireService'
import QuestionnaireReviewForm from './QuestionnaireReviewForm'
import { VendorQuestionnaireStatuses } from './types'

// Define interface for attachment objects
interface Attachment {
  id: number
  vendor_questionnaire_id: number
  field_id: string
  field_source?: string
  original_filename: string
  file_type: string
  file_size: number
  uploaded_at: string
  questionnaire_name?: string // Added to store the questionnaire name
}

// Helper function to format expiry minutes into a human-readable format
const formatExpiryTime = (minutes?: number): string => {
  if (minutes === undefined) return 'Not set'

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  if (hours === 0) {
    return `${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`
  }
  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`
  }
  return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minute${
    remainingMinutes !== 1 ? 's' : ''
  }`
}

// Status mapping for questionnaire status
const getStatusText = (status: number): string => {
  switch (status) {
    case VendorQuestionnaireStatuses.Pending:
      return 'Needs Review'
    case VendorQuestionnaireStatuses.InProgress:
      return 'In Progress'
    case VendorQuestionnaireStatuses.VendorSubmitted:
      return 'Submitted'
    case VendorQuestionnaireStatuses.Completed:
      return 'Completed'
    case VendorQuestionnaireStatuses.Expired:
      return 'Expired'
    case VendorQuestionnaireStatuses.Cancelled:
      return 'Cancelled'
    case VendorQuestionnaireStatuses.Rejected:
      return 'Rejected'
    case VendorQuestionnaireStatuses.Resubmitted:
      return 'Resubmitted'
    case VendorQuestionnaireStatuses.ClarificationRequested:
      return 'Clarification Requested'
    default:
      return 'Unknown'
  }
}

interface VendorDetailsDialogProps {
  open: boolean
  onClose: () => void
  vendorId: string | number | null
  onChanges?: () => void
}

const VendorDetailsDialog: React.FC<VendorDetailsDialogProps> = ({
  open,
  onClose,
  vendorId,
  onChanges,
}) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()

  const [vendor, setVendor] = useState<any>(null)
  const [questionnaires, setQuestionnaires] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<any>(null)
  const [questionnaireDetails, setQuestionnaireDetails] = useState<any>(null)
  const [users, setUsers] = useState<any[]>([])
  const [viewSource, setViewSource] = useState<'vendor' | 'review' | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [fieldLabelMap, setFieldLabelMap] = useState<Record<string, string>>({})

  // Load vendor details
  const loadVendorDetails = useCallback(async () => {
    if (!vendorId) return

    try {
      setLoading(true)

      // Fetch vendor details from the API
      const { data } = await dataProvider.getOne('vendors', { id: vendorId })
      setVendor(data)
    } catch (error) {
      console.error('Error loading vendor details:', error)
      notify('Error loading vendor details', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [vendorId, dataProvider, notify])

  // Load questionnaires
  const loadQuestionnaires = useCallback(async () => {
    if (!vendorId) return

    try {
      setLoading(true)

      // Fetch vendor questionnaires from the API
      const { data } = await dataProvider.custom(
        `questionnaires/vendors/${vendorId}`,
        { method: 'GET' }
      )

      // Sort questionnaires by created date (oldest first)
      const sortedQuestionnaires = [...(data || [])].sort((a, b) => {
        const dateA = new Date(a.created_at || 0).getTime()
        const dateB = new Date(b.created_at || 0).getTime()
        return dateA - dateB // Ascending order (oldest first)
      })

      setQuestionnaires(sortedQuestionnaires)
    } catch (error) {
      console.error('Error loading questionnaires:', error)
      setQuestionnaires([])
    } finally {
      setLoading(false)
    }
  }, [vendorId, dataProvider])

  // Load users with supervisor flag
  const loadUsers = useCallback(async () => {
    try {
      // Get all users
      const { data: allUsers } = await dataProvider.getList('users', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      })

      setUsers(allUsers || [])
    } catch (error) {
      console.error('Error loading users:', error)
      notify('Error loading users', { type: 'error' })
    }
  }, [dataProvider, notify])

  // Helper function to construct attachment download URL
  const getAttachmentDownloadUrl = (attachment: Attachment) => {
    // Add a timestamp to prevent caching
    const timestamp = new Date().getTime()
    // Use the public endpoint to ensure it works even if the user is not authenticated
    return `http://localhost:8888/api/v1/questionnaires/public/vendors/${vendorId}/${attachment.vendor_questionnaire_id}/attachments/${attachment.id}?download=true&t=${timestamp}`
  }

  // Load all attachments for all questionnaires and build field label mappings
  const loadAllAttachments = useCallback(async () => {
    if (!vendorId || questionnaires.length === 0) return

    try {
      const allAttachments: Attachment[] = []
      const fieldLabels: Record<string, string> = {}

      // Fetch attachments and questionnaire templates for each questionnaire
      await Promise.all(
        questionnaires.map(async (questionnaire) => {
          try {
            // Fetch attachments
            const { data: attachmentsData } = await dataProvider.custom(
              `questionnaires/vendors/${vendorId}/${questionnaire.id}/attachments`,
              { method: 'GET' }
            )

            // Add questionnaire name to each attachment
            const attachmentsWithQuestionnaireName = (
              attachmentsData || []
            ).map((attachment: Attachment) => ({
              ...attachment,
              questionnaire_name:
                questionnaire.questionnaire?.name || 'Unknown Questionnaire',
            }))

            allAttachments.push(...attachmentsWithQuestionnaireName)

            // Fetch questionnaire template to get field labels
            try {
              const { data: questionnaireTemplate } = await dataProvider.custom(
                `questionnaires/${questionnaire.questionnaire_id}/organisations/current`,
                { method: 'GET' }
              )

              // Build mapping from field source to field label
              if (questionnaireTemplate?.questionnaire) {
                questionnaireTemplate.questionnaire.forEach((section: any) => {
                  if (section.fields) {
                    section.fields.forEach((field: any) => {
                      // Map both field.source and field_id to field.label
                      fieldLabels[field.source] = field.label
                    })
                  }
                })
              }
            } catch (templateError) {
              console.error(
                `Error loading questionnaire template for ${questionnaire.id}:`,
                templateError
              )
              // Continue even if template fetch fails
            }
          } catch (error) {
            console.error(
              `Error loading attachments for questionnaire ${questionnaire.id}:`,
              error
            )
            // Continue with other questionnaires even if one fails
          }
        })
      )

      setAttachments(allAttachments)
      setFieldLabelMap(fieldLabels)
    } catch (error) {
      console.error('Error loading attachments:', error)
      notify('Error loading attachments', { type: 'warning' })
    }
  }, [vendorId, questionnaires, dataProvider, notify])

  // Load vendor details and questionnaires when the dialog is opened
  useEffect(() => {
    if (open && vendorId) {
      loadVendorDetails()
      loadQuestionnaires()
      loadUsers()
      // Reset hasChanges when dialog is opened
      setHasChanges(false)
    } else {
      // Reset state when dialog is closed
      setVendor(null)
      setQuestionnaires([])
      setSelectedQuestionnaire(null)
      setQuestionnaireDetails(null)
      setAttachments([])
    }
  }, [open, vendorId, loadVendorDetails, loadQuestionnaires, loadUsers])

  // Load attachments when questionnaires are loaded
  useEffect(() => {
    if (questionnaires.length > 0 && vendorId) {
      loadAllAttachments()
    }
  }, [questionnaires, vendorId, loadAllAttachments])

  // Custom onClose handler that will call onChanges if hasChanges is true
  const handleClose = useCallback(() => {
    // If changes were made and onChanges is provided, call it
    if (hasChanges && onChanges) {
      onChanges()
    }
    // Call the original onClose prop
    onClose()
  }, [hasChanges, onChanges, onClose])

  // Handle deleting a questionnaire
  const handleDeleteQuestionnaire = async (questionnaireId: string) => {
    if (!vendorId) return

    try {
      // Delete the questionnaire using the API
      await dataProvider.custom(
        `questionnaires/vendors/${vendorId}/${questionnaireId}`,
        { method: 'DELETE' }
      )

      // Update state by removing the deleted questionnaire
      setQuestionnaires((prevQuestionnaires) =>
        prevQuestionnaires.filter((q) => q.id !== questionnaireId)
      )

      // Mark that changes have been made
      setHasChanges(true)

      notify('Questionnaire deleted successfully', { type: 'success' })
    } catch (error) {
      console.error('Error deleting questionnaire:', error)
      notify('Error deleting questionnaire', { type: 'error' })
    }
  }

  // Handle sending a questionnaire
  const handleSendQuestionnaire = async (questionnaireId: string) => {
    if (!vendorId) return

    try {
      // Use the shared sendQuestionnaire function
      const result = await sendQuestionnaire({
        dataProvider,
        questionnaireId,
        vendorIds: [Number(vendorId)], // Convert id to number and wrap in array
        notify, // Pass the notify function for error handling
      })

      if (result.successCount > 0) {
        // Update state by updating the status of the questionnaire
        setQuestionnaires((prevQuestionnaires) =>
          prevQuestionnaires.map((q) => {
            if (q.id === questionnaireId) {
              return { ...q, status: 1 }
            }
            return q
          })
        )

        // Mark that changes have been made
        setHasChanges(true)

        notify('Questionnaire sent successfully', { type: 'success' })
      } else if (result.errorCount === 0) {
        // If no success but no error was thrown, show a generic error
        notify('Error sending questionnaire', { type: 'error' })
      }
      // Errors are handled by the sendQuestionnaire function via the notify callback
    } catch (error) {
      console.error('Error sending questionnaire:', error)
      notify('Error sending questionnaire', { type: 'error' })
    }
  }

  // Handle viewing a completed questionnaire
  const handleViewQuestionnaire = async (questionnaireId: string) => {
    if (!vendorId) return

    try {
      // Find the questionnaire in the current state
      const questionnaireToView = questionnaires.find(
        (q) => q.id === questionnaireId
      )

      if (!questionnaireToView) {
        notify('Questionnaire not found', { type: 'error' })
        return
      }

      // Set the selected questionnaire and view source
      setSelectedQuestionnaire(questionnaireToView)
      setViewSource('vendor')

      // Fetch the questionnaire template to get the structure
      try {
        const { data: questionnaireTemplate } = await dataProvider.custom(
          `questionnaires/${questionnaireToView.questionnaire_id}/organisations/current`,
          { method: 'GET' }
        )

        // Set the questionnaire details with both the template and the completed data
        setQuestionnaireDetails({
          template: questionnaireTemplate,
          completed: questionnaireToView.completed_questionnaire,
        })

        // Open the dialog
        setViewDialogOpen(true)
      } catch (error) {
        console.error('Error fetching questionnaire template:', error)
        notify('Error fetching questionnaire details', { type: 'error' })
      }
    } catch (error) {
      console.error('Error viewing questionnaire:', error)
      notify('Error viewing questionnaire', { type: 'error' })
    }
  }

  // Function to close the view dialog
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setSelectedQuestionnaire(null)
    setQuestionnaireDetails(null)
    setViewSource(null)
  }

  if (loading) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '90%',
            maxWidth: '1200px',
            height: '90vh',
            maxHeight: '900px',
          },
        }}
      >
        <DialogTitle>Loading...</DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
          >
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '90%',
            maxWidth: '1200px',
            height: '90vh',
            maxHeight: '900px',
          },
        }}
      >
        <DialogTitle>Questionnaires for {vendor?.name || 'Vendor'}</DialogTitle>
        <DialogContent>
          {questionnaires.length === 0 ? (
            <Box
              padding={2}
              bgcolor="#f5f5f5"
              borderRadius={1}
              textAlign="center"
            >
              No questionnaires found for this vendor.
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Questionnaire Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Risk Score Range</TableCell>
                    <TableCell>Risk Score</TableCell>
                    <TableCell>Expiry Time</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questionnaires.map((questionnaire) => (
                    <TableRow key={questionnaire.questionnaire_id}>
                      <TableCell>
                        {questionnaire.questionnaire?.name ||
                          questionnaire.questionnaire_id}
                      </TableCell>
                      <TableCell>
                        {(
                          questionnaire.questionnaire?.type || 'N/A'
                        ).toUpperCase()}
                      </TableCell>
                      <TableCell>
                        {getStatusText(questionnaire.status)}
                      </TableCell>
                      <TableCell>
                        {questionnaire.questionnaire?.type === 'preliminary'
                          ? 'N/A'
                          : questionnaire.questionnaire?.min_risk_score !==
                              undefined &&
                            questionnaire.questionnaire?.max_risk_score !==
                              undefined
                          ? `${questionnaire.questionnaire.min_risk_score} - ${questionnaire.questionnaire.max_risk_score}`
                          : 'Not calculated'}
                      </TableCell>
                      <TableCell>
                        {questionnaire.score !== null &&
                        questionnaire.score !== undefined
                          ? questionnaire.score.toFixed(1)
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {formatExpiryTime(
                          questionnaire.questionnaire?.expiry_minutes
                        )}
                      </TableCell>
                      <TableCell>
                        {questionnaire.created_at
                          ? new Date(
                              questionnaire.created_at
                            ).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {users.find(
                          (user) => user.id === questionnaire.agent_user_id
                        )?.name || questionnaire.agent_user_id}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          {questionnaire.status ===
                            VendorQuestionnaireStatuses.Pending && (
                            <MuiButton
                              variant="outlined"
                              size="small"
                              onClick={() =>
                                handleSendQuestionnaire(questionnaire.id)
                              }
                              title="Send Questionnaire"
                              startIcon={<SendIcon />}
                            >
                              Send
                            </MuiButton>
                          )}
                          {(questionnaire.status ===
                            VendorQuestionnaireStatuses.VendorSubmitted ||
                            questionnaire.status ===
                              VendorQuestionnaireStatuses.Completed ||
                            questionnaire.status ===
                              VendorQuestionnaireStatuses.Cancelled ||
                            questionnaire.status ===
                              VendorQuestionnaireStatuses.Rejected) && (
                            <MuiButton
                              variant="outlined"
                              size="small"
                              onClick={() =>
                                handleViewQuestionnaire(questionnaire.id)
                              }
                              title="View Completed Questionnaire"
                              startIcon={<VisibilityIcon />}
                            >
                              View
                            </MuiButton>
                          )}
                          <MuiButton
                            variant="outlined"
                            size="small"
                            onClick={() =>
                              handleDeleteQuestionnaire(questionnaire.id)
                            }
                            title="Delete Questionnaire"
                            color="error"
                            startIcon={<DeleteIcon />}
                          >
                            Delete
                          </MuiButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Stored Documents Section */}
          <Box mt={4} mb={2}>
            <Divider />
            <Typography variant="h6" mt={2} mb={2}>
              Stored Documents
            </Typography>
            {attachments.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Questionnaire</TableCell>
                      <TableCell>Field Source</TableCell>
                      <TableCell>File Size</TableCell>
                      <TableCell>Uploaded Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attachments.map((attachment) => (
                      <TableRow key={attachment.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <AttachFileIcon fontSize="small" sx={{ mr: 1 }} />
                            <Link
                              href={getAttachmentDownloadUrl(attachment)}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                            >
                              {attachment.original_filename}
                            </Link>
                          </Box>
                        </TableCell>
                        <TableCell>{attachment.questionnaire_name}</TableCell>
                        <TableCell>
                          {fieldLabelMap[
                            attachment.field_source || attachment.field_id
                          ] ||
                            attachment.field_source ||
                            attachment.field_id}
                        </TableCell>
                        <TableCell>
                          {Math.round(attachment.file_size / 1024)} KB
                        </TableCell>
                        <TableCell>
                          {new Date(attachment.uploaded_at).toLocaleDateString(
                            undefined,
                            {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            }
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                padding={2}
                bgcolor="#f5f5f5"
                borderRadius={1}
                textAlign="center"
              >
                No documents found for this vendor.
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleClose} variant="outlined">
            Close
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* View Questionnaire Dialog */}
      <QuestionnaireReviewForm
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        selectedQuestionnaire={selectedQuestionnaire}
        viewSource={viewSource}
        onRefresh={() => {
          loadQuestionnaires()
          refresh()
          // Mark that changes have been made
          setHasChanges(true)
        }}
      />
    </>
  )
}

export default VendorDetailsDialog
